import {inject, Injectable, Input} from '@angular/core';
import {EventHttpService} from "../../../management/x-http-requests/event-http.service";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {EmployeeEvent} from "../../../models/event/employee-event";
import {BehaviorSubject, Observable} from "rxjs";
import {AlertLevel} from "../../../enums/alert-level";
import {CalendarEntry} from "../../x-components/small-calendar/x-models/calendar-entry";
import {CalendarDay} from "../../x-components/small-calendar/x-models/calendar-day";
import {State} from "../../../enums/employee-event-state";
import {Tuple} from "../../../models/_generic/tuple";
import {EmployeeEventResponseNl} from "../../../models/event/employee-event-response-nl";
import {CompanyTimeZoneService} from "../../../_shared/time/company-time-zone.service";
import {companyDayKey, companyDayStartInstant, pickerDateToDayKey} from "../../../_shared/time/company-day";

@Injectable({
  providedIn: 'root'
})
export class EventService {
  constructor(private eventHttpService: EventHttpService, private gas: GlobalAlertService) {
    this.events = new BehaviorSubject<EmployeeEvent[] | undefined | null>(undefined);
    this.events_sub = this.events.asObservable();

    this.events_sub
      .subscribe({
        next: (events) => {
          if (events === null) {
            this.gas.createAndShow("Fehler", "Veranstaltungen konnten nicht geladen werden.", AlertLevel.error);
            return;
          }
          if (events === undefined) return;

          // Response normalisation used to live here. It moved into `merge()` because it now has to survive
          // ACROSS payloads: a payload fetched for a window starting after an event deliberately carries no
          // response, and re-running "no response ⇒ fresh placeholder" over the whole accumulated set would
          // wipe an answer the previous payload already delivered.
          this.map();
          this.filter();
        }
      })
  }

  public events: BehaviorSubject<EmployeeEvent[] | undefined | null>;

  public get events_all() {
    return this.events.value;
  }

  public events_sub: Observable<EmployeeEvent[] | undefined | null>;
  public events_filtered: EmployeeEvent[] = [];

  public calendarEntries: CalendarEntry[] = [];

  private _selectedDay: CalendarDay = new CalendarDay(new Date(), false);
  public get selectedDay() {
    return this._selectedDay;
  }

  public set selectedDay(value: CalendarDay) {
    this._selectedDay = value;
    this.filter();
  }


  public calendarRange: Tuple<Date, Date> = new Tuple<Date, Date>();

  /**
   * The company's time zone: the reference for every calendar day on this screen (#656). The month picker is built
   * from browser-local dates and stays that way (display); what changes is WHICH picker day an event is on and
   * where a day starts — both are company days.
   */
  private readonly companyTimeZone = inject(CompanyTimeZoneService);

  map() {
    if (!this.events_all) return;
    const timeZone = this.companyTimeZone.timeZone();
    this.calendarEntries = this.events_all.map(x => new CalendarEntry(x.start, x.end, x.color, x.id, timeZone));
  }

  @Input() filterFunc: (e: EmployeeEvent) => boolean = (e) => true;

  filter() {
    if (!this.events_all) {
      this.events_filtered = [];
      return;
    }

    // "From the selected day on" in company days: an event starting 00:30 on the selected day (company zone) is
    // listed even for a viewer whose browser still says the day before, and 23:30 the day before is not.
    const timeZone = this.companyTimeZone.timeZone();
    const selected = pickerDateToDayKey(this._selectedDay.date);
    this.events_filtered = Object.assign([], this.events_all.filter(x =>
      companyDayKey(x.start, timeZone) >= selected && this.filterFunc(x)));
  }

  /**
   * Every event received so far, keyed by id, in arrival order — the accumulator behind {@link events_all}.
   *
   * Payloads MERGE rather than replace. The month picker's dots are drawn from every held event, so paging
   * back to March and then forward again must not throw March away: replacing would make the dots flicker out
   * on every navigation and force a re-fetch to get them back. Keying by id is also what makes the overlapping
   * windows safe — a re-read of the forward tail (see {@link load}) updates rows instead of duplicating dots.
   */
  private readonly eventsById = new Map<string, EmployeeEvent>();

  /**
   * The employee's REAL availability answers, keyed by event id. A placeholder is never stored here.
   *
   * This is the memory that makes the narrow responses window safe: an event returned without a response
   * means "not asked for in this window", never "answer withdrawn", so an answer already delivered is
   * re-attached to every later payload of the same event.
   */
  private readonly responsesById = new Map<string, EmployeeEventResponseNl>();

  /** Lower edge of the loaded EVENT window; coverage is `[loadedEventsFrom, ∞)`. */
  private loadedEventsFrom: Date | undefined;

  /** Lower edge of the loaded RESPONSE window; coverage is `[loadedResponsesFrom, ∞)`. */
  private loadedResponsesFrom: Date | undefined;

  /**
   * Ensures the held data covers events from `from` forward and availability answers from `responsesFrom`
   * forward — by default both the calendar's selected day.
   *
   * **Two independent edges, each widening downwards only.** Coverage is always two half-open intervals
   * `[loadedEventsFrom, ∞)` and `[loadedResponsesFrom, ∞)`, so "is a request needed" is just "is either
   * requested edge below the one we hold". Nothing is ever skipped that has not already been fetched:
   *
   * - **Only the events edge moves down** (paging the picker to an earlier month): the request is exactly the
   *   missing prefix `[wanted, loadedEventsFrom)`. Coverage stays contiguous, the forward tail is not re-sent,
   *   and the server skips its response query entirely because no event in a past month is inside the
   *   response window.
   * - **The responses edge moves down** (selecting an earlier day): the window has to stay open above,
   *   because the answers we are missing hang off events we already hold. Those events come back and merge
   *   onto themselves; the responses are what the round-trip is actually for.
   * - **Neither moves** (paging forward, re-visiting a month): no request at all.
   *
   * Both edges are claimed when the request is ISSUED, so two rapid clicks do not both fire, and rolled back
   * on failure so a retry is possible.
   */
  load(from: Date = this._selectedDay.date, responsesFrom: Date = this._selectedDay.date) {
    // The day bounds need the company zone. Once it is known `load()` answers synchronously, so the edge claims
    // below still happen in call order and two rapid clicks cannot both fire; before that, queued calls replay in
    // order when the one zone request resolves.
    this.companyTimeZone.load().subscribe(timeZone => this.loadWindows(from, responsesFrom, timeZone));
  }

  private loadWindows(from: Date, responsesFrom: Date, timeZone: string) {
    const wantedEvents = EventService.companyDayStart(from, timeZone);
    const wantedResponses = EventService.companyDayStart(responsesFrom, timeZone);

    const needEvents = !this.loadedEventsFrom || wantedEvents < this.loadedEventsFrom;
    const needResponses = !this.loadedResponsesFrom || wantedResponses < this.loadedResponsesFrom;
    if (!needEvents && !needResponses) return;

    const start = needEvents && needResponses
      ? (wantedEvents < wantedResponses ? wantedEvents : wantedResponses)
      : (needEvents ? wantedEvents : wantedResponses);
    // `end` is exclusive and meets the edge we already hold, so the two windows abut with no gap and no
    // overlap. Only a responses-driven fetch has to stay open above.
    const end = needResponses ? null : this.loadedEventsFrom ?? null;

    const heldEvents = this.loadedEventsFrom;
    const heldResponses = this.loadedResponsesFrom;
    if (!this.loadedEventsFrom || start < this.loadedEventsFrom) this.loadedEventsFrom = start;
    if (needResponses) this.loadedResponsesFrom = wantedResponses;

    this.eventHttpService.getPublicEvents(start, end, wantedResponses)
      .subscribe({
        next: (events) => {
          this.events.next(this.merge(events));
        },
        error: (err) => {
          console.error(err);
          this.loadedEventsFrom = heldEvents;
          this.loadedResponsesFrom = heldResponses;
          this.events.next(null);
        }
      })
  }

  /**
   * The instant the picker day `day` begins in the COMPANY zone. `day` is a browser-local picker date, so its
   * calendar date is read from its local fields; the start of that date is then resolved in the company zone
   * (#656). Local midnight was right only for viewers inside the company's zone.
   */
  private static companyDayStart(day: Date, timeZone: string): Date {
    return companyDayStartInstant(pickerDateToDayKey(day), timeZone);
  }

  /**
   * Folds one payload into the accumulated set and returns the whole of it.
   *
   * Two rules, and the second is the one the split windows depend on:
   * 1. An event already held is UPDATED, not duplicated — the Map is keyed by id.
   * 2. A real response is remembered per event id and re-attached on every later payload. An event returned
   *    with no response is one the server was not asked about (it starts before the responses window), so it
   *    falls back to the fresh `State.New` placeholder the card renders as unanswered — the same placeholder
   *    the old inline normalisation created, only now it can never overwrite a known answer.
   */
  private merge(incoming: EmployeeEvent[]): EmployeeEvent[] {
    for (const event of incoming) {
      if (event.employeeEventResponse) this.responsesById.set(event.id, event.employeeEventResponse);

      const known = this.responsesById.get(event.id);
      if (known) {
        event.employeeEventResponse = known;
        known.new = State.Locked;
      } else {
        event.employeeEventResponse = new EmployeeEventResponseNl(State.New);
      }

      this.eventsById.set(event.id, event);
    }

    return [...this.eventsById.values()];
  }

  isPast(event: EmployeeEvent): boolean {
    return event.start <= new Date();
  }

  isEditable(end
             :
             Date
  ):
    boolean {
    return end > new Date();
  }

  send(event
       :
       EmployeeEvent
  ) {
    this.eventHttpService.sendEmployeeEventResponse(event.shardId, event.id, event.employeeEventResponse).subscribe({
      next: (response) => {
        this.gas.createAndShow("Erfolgreich", "Die Verfügbarkeit wurde erfolgreich gespeichert.", AlertLevel.success);
        event.employeeEventResponse = response;
        event.employeeEventResponse.new = State.Locked;
        // Record it, or the next merge re-attaches the answer this one just replaced.
        this.responsesById.set(event.id, response);
      },
      error: (err) => {
        console.error(err);
        event.employeeEventResponse.active = undefined;
        this.gas.createAndShow("Fehler", "Beim Speichern der Verfügbarkeit ist ein Fehler aufgetreten.", AlertLevel.error);
      }
    })
  }

  setResponse(event
              :
              EmployeeEvent, available
              :
              boolean, comment
              :
              string
  ) {
    if (event.employeeEventResponse.active === available && event.employeeEventResponse.comments === comment) return;
    event.employeeEventResponse.active = available;
    event.employeeEventResponse.comments = comment;
    this.send(event);
  }
}
